import { NavLink } from "react-router-dom";
import { useEffect, useRef } from "react";
import type { LucideIcon } from "lucide-react";
import { useI18n } from "../lib/i18n";

/**
 * Bottom tab bar for phones and tablets.
 *
 * Below `lg` the sidebar is hidden and there was no navigation at all, so this
 * is the only way to move between pages on a phone.
 *
 * It scrolls horizontally rather than collapsing into a "More" sheet: the
 * journal has ten destinations, and a scrolling row keeps every one of them a
 * single tap away instead of burying six behind a menu. The active tab is
 * scrolled into view on mount and on navigation, so the current page is never
 * off-screen.
 *
 * Glass: translucent ground + blur, hairline top edge, and the home-indicator
 * area padded out with the safe-area inset so the row never sits under it.
 */
export default function MobileTabBar({
  items,
}: {
  items: { to: string; labelKey: string; Icon: LucideIcon }[];
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const active = ref.current?.querySelector("[aria-current='page']");
    active?.scrollIntoView({ inline: "center", block: "nearest" });
  });

  return (
    <nav
      ref={ref}
      aria-label={t("nav.dashboard")}
      className="lg:hidden fixed inset-x-0 bottom-0 z-40 overflow-x-auto overscroll-x-contain"
      style={{
        backgroundColor: "rgb(var(--bg-950) / 0.72)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        backdropFilter: "blur(20px) saturate(180%)",
        borderTop: "1px solid rgb(var(--border-soft))",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        scrollbarWidth: "none",
      }}
    >
      <ul className="flex items-stretch gap-1 px-2 py-1.5 w-max min-w-full">
        {items.map(({ to, labelKey, Icon }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                "flex flex-col items-center justify-center gap-0.5 rounded-xl px-3 py-1.5 min-w-[4.25rem] transition nav-item" +
                (isActive ? " nav-item--active" : "")
              }
            >
              <Icon className="w-5 h-5 shrink-0" />
              <span className="text-[10px] font-semibold leading-tight whitespace-nowrap">
                {t(labelKey)}
              </span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
